import React, { useEffect, useRef, useState } from 'react'

const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.nursingmedicaldictionary"
const APP_STORE_URL = "https://apps.apple.com/us/app/medical-nursing-dictionary/id6670346210"
export default function Home() {
  const [scrollY, setScrollY] = useState(0)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const features = [
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
        </svg>
      ),
      title: "Smart Search",
      desc: "Instantly find definitions for thousands of medical terms with intelligent autocomplete."
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28">
          <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
        </svg>
      ),
      title: "Body Systems",
      desc: "Organized by anatomy — cardiovascular, neurology, endocrine, and every system covered."
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28">
          <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/><path d="M3 9h6"/><path d="M3 15h6"/>
        </svg>
      ),
      title: "Abbreviations",
      desc: "Comprehensive A–Z reference of clinical abbreviations used in hospitals and clinics."
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28">
          <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
        </svg>
      ),
      title: "Specialties",
      desc: "Explore all medical specialties — from cardiology to oncology — with their specialist roles."
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28">
          <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
        </svg>
      ),
      title: "Prometric Prep",
      desc: "Practice exams for RN and medical assistant licensing. Track your score over time."
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="28" height="28">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      ),
      title: "For All Roles",
      desc: "Nurses, doctors, students, and allied health professionals — this app is built for you."
    }
  ]

  const stats = [
    { value: "50K+", label: "Terms & Definitions" },
    { value: "10K+", label: "Abbreviations" },
    { value: "30+", label: "Medical Specialties" },
    { value: "4.8★", label: "App Store Rating" },
  ]

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  
        :root {
          --bg: #ffffff;
          --surface: #f4f4f4;
          --surface2: #e0e0e0;
          --teal: #2ab7be;
          --teal-dim: rgba(42,183,190,0.12);
          --teal-glow: rgba(42,183,190,0.3);
          --blue: #0056b3;
          --text: #333333;
          --text-muted: #666666;
          --border: rgba(0,0,0,0.1);
          --font-display: 'Arial', sans-serif;
          --font-body: 'Arial', sans-serif;
          --radius: 16px;
        }

        html { scroll-behavior: smooth; }

        body {
          background: var(--bg);
          color: var(--text);
          font-family: var(--font-body);
          font-size: 16px;
          line-height: 1.6;
          overflow-x: hidden;
        }

        /* NAV */
        .nav {
          position: fixed; top: 0; left: 0; right: 0; z-index: 100;
          padding: 0 5%;
          height: 72px;
          display: flex; align-items: center; justify-content: space-between;
          transition: background 0.3s, backdrop-filter 0.3s, box-shadow 0.3s;
        }
        .nav.scrolled {
          background: rgba(255,255,255,0.9);
          backdrop-filter: blur(20px);
          box-shadow: 0 1px 0 var(--border);
        }
        .nav-logo {
          display: flex; align-items: center; gap: 10px;
          font-family: var(--font-display); font-size: 1.2rem; font-weight: 700;
          color: var(--text); text-decoration: none;
        }
        .nav-logo-icon {
          width: 36px; height: 36px; border-radius: 10px;
          background: linear-gradient(135deg, var(--teal), var(--blue));
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .nav-links { display: flex; align-items: center; gap: 32px; list-style: none; }
        .nav-links a {
          color: var(--text-muted); text-decoration: none; font-size: 0.9rem;
          font-weight: 500; transition: color 0.2s;
        }
        .nav-links a:hover { color: var(--text); }
        .btn-nav {
          background: var(--teal); color: #ffffff !important;
          padding: 9px 22px; border-radius: 8px; font-weight: 600 !important;
          font-size: 0.85rem !important; transition: opacity 0.2s, transform 0.2s !important;
        }
        .btn-nav:hover { opacity: 0.9 !important; transform: translateY(-1px) !important; }
        .hamburger {
          display: none; flex-direction: column; gap: 5px; cursor: pointer;
          background: none; border: none; padding: 4px;
        }
        .hamburger span {
          display: block; width: 24px; height: 2px;
          background: var(--text); border-radius: 2px; transition: 0.3s;
        }
        .mobile-menu {
          display: none; position: fixed; top: 72px; left: 0; right: 0;
          background: rgba(255,255,255,0.97); backdrop-filter: blur(20px);
          padding: 24px 5%; border-bottom: 1px solid var(--border);
          flex-direction: column; gap: 20px; z-index: 99;
        }
        .mobile-menu.open { display: flex; }
        .mobile-menu a {
          color: var(--text-muted); text-decoration: none; font-size: 1rem;
          font-weight: 500; padding: 4px 0; transition: color 0.2s;
        }
        .mobile-menu a:hover { color: var(--text); }

        /* HERO */
        .hero {
          min-height: 100vh;
          display: flex; align-items: center;
          padding: 100px 5% 80px;
          position: relative; overflow: hidden;
        }
        .hero-bg {
          position: absolute; inset: 0; z-index: 0; pointer-events: none;
        }
        .hero-gradient {
          position: absolute; top: -200px; right: -100px;
          width: 700px; height: 700px; border-radius: 50%;
          background: radial-gradient(circle, rgba(0,212,200,0.08) 0%, transparent 70%);
        }
        .hero-gradient-2 {
          position: absolute; bottom: -100px; left: -150px;
          width: 500px; height: 500px; border-radius: 50%;
          background: radial-gradient(circle, rgba(26,110,248,0.07) 0%, transparent 70%);
        }
        .hero-grid {
          position: absolute; inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px);
          background-size: 60px 60px;
          mask-image: radial-gradient(ellipse at 60% 40%, black 0%, transparent 70%);
        }
        .hero-inner {
          position: relative; z-index: 1;
          max-width: 1200px; margin: 0 auto; width: 100%;
          display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center;
        }
        .hero-badge {
          display: inline-flex; align-items: center; gap: 8px;
          background: var(--teal-dim); border: 1px solid rgba(0,212,200,0.2);
          border-radius: 100px; padding: 6px 16px; font-size: 0.8rem;
          color: var(--teal); font-weight: 500; margin-bottom: 24px;
          letter-spacing: 0.04em; text-transform: uppercase;
        }
        .hero-badge-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: var(--teal); animation: pulse 2s infinite;
        }
        @keyframes pulse {
          0%,100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.3); }
        }
        .hero-title {
          font-family: var(--font-display); font-size: clamp(2.6rem, 5vw, 4.2rem);
          font-weight: 800; line-height: 1.1; letter-spacing: -0.02em;
          margin-bottom: 24px;
        }
        .hero-title span { color: var(--teal); }
        .hero-desc {
          font-size: 1.1rem; color: var(--text-muted); line-height: 1.7;
          max-width: 480px; margin-bottom: 40px;
        }
        .hero-actions { display: flex; gap: 16px; flex-wrap: wrap; align-items: center; }
        .btn-primary-lg {
          display: inline-flex; align-items: center; gap: 10px;
          background: var(--teal); color: #ffffff;
          padding: 14px 28px; border-radius: 12px; font-weight: 700;
          font-size: 0.95rem; text-decoration: none;
          transition: transform 0.2s, box-shadow 0.2s;
          box-shadow: 0 0 0 0 var(--teal-glow);
        }
        .btn-primary-lg:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 32px var(--teal-glow);
        }
        .btn-ghost-lg {
          display: inline-flex; align-items: center; gap: 10px;
          background: transparent; color: var(--text);
          padding: 14px 28px; border-radius: 12px; font-weight: 500;
          font-size: 0.95rem; text-decoration: none;
          border: 1px solid var(--border);
          transition: background 0.2s, border-color 0.2s;
        }
        .btn-ghost-lg:hover { background: var(--surface); border-color: rgba(255,255,255,0.15); }

        /* PHONE MOCKUP */
        .hero-visual {
          position: relative; display: flex; justify-content: center;
        }
        .phone-wrap {
          position: relative; width: 280px;
          animation: float 4s ease-in-out infinite;
        }
        @keyframes float {
          0%,100% { transform: translateY(0); }
          50% { transform: translateY(-14px); }
        }
        .phone-frame {
          width: 100%;
          background: var(--surface2);
          border-radius: 40px;
          border: 1.5px solid rgba(255,255,255,0.1);
          padding: 16px;
          box-shadow:
            0 40px 80px rgba(0,0,0,0.5),
            inset 0 1px 0 rgba(255,255,255,0.08),
            0 0 60px rgba(0,212,200,0.06);
          overflow: hidden;
        }
        .phone-screen {
          border-radius: 28px; background: var(--bg);
          overflow: hidden; aspect-ratio: 9/19;
          display: flex; flex-direction: column;
          border: 1px solid rgba(255,255,255,0.05);
        }
        .phone-topbar {
          background: var(--surface); padding: 14px 16px 10px;
          display: flex; align-items: center; gap: 10px;
          border-bottom: 1px solid var(--border);
        }
        .phone-app-icon {
          width: 32px; height: 32px; border-radius: 8px;
          background: linear-gradient(135deg, var(--teal), var(--blue));
          flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
        }
        .phone-app-name {
          font-family: var(--font-display); font-size: 0.7rem; font-weight: 700;
          color: var(--text); line-height: 1.1;
        }
        .phone-app-sub { font-size: 0.55rem; color: var(--text-muted); }
        .phone-search {
          margin: 12px; padding: 9px 14px;
          background: var(--surface2); border-radius: 10px;
          font-size: 0.65rem; color: var(--text-muted);
          display: flex; align-items: center; gap: 8px;
          border: 1px solid var(--border);
        }
        .phone-term-list { flex: 1; padding: 4px 12px; overflow: hidden; }
        .phone-term {
          padding: 10px 0; border-bottom: 1px solid var(--border);
          display: flex; align-items: flex-start; gap: 8px;
        }
        .phone-term:last-child { border-bottom: none; }
        .phone-term-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: var(--teal); margin-top: 4px; flex-shrink: 0;
        }
        .phone-term-name { font-size: 0.62rem; font-weight: 600; color: var(--text); margin-bottom: 2px; }
        .phone-term-def { font-size: 0.54rem; color: var(--text-muted); line-height: 1.4; }
        .phone-glow {
          position: absolute; bottom: -40px; left: 50%; transform: translateX(-50%);
          width: 200px; height: 200px; border-radius: 50%;
          background: radial-gradient(circle, rgba(0,212,200,0.15) 0%, transparent 70%);
          pointer-events: none;
        }
        .phone-badge {
          position: absolute; right: -20px; top: 60px;
          background: var(--surface2); border: 1px solid rgba(0,212,200,0.3);
          border-radius: 12px; padding: 10px 14px;
          display: flex; align-items: center; gap: 8px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.4);
          animation: float 4s ease-in-out infinite 2s;
        }
        .phone-badge-icon { width: 32px; height: 32px; border-radius: 8px; background: var(--teal-dim); display: flex; align-items: center; justify-content: center; color: var(--teal); }
        .phone-badge-text { font-size: 0.62rem; }
        .phone-badge-num { font-family: var(--font-display); font-weight: 800; font-size: 0.95rem; color: var(--teal); line-height: 1; }
        .phone-badge-label { color: var(--text-muted); }
        .phone-badge2 {
          position: absolute; left: -30px; bottom: 100px;
          background: var(--surface2); border: 1px solid rgba(26,110,248,0.3);
          border-radius: 12px; padding: 10px 14px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.4);
          animation: float 4s ease-in-out infinite 1s;
        }
        .phone-badge2-stars { color: #fbbf24; font-size: 0.7rem; margin-bottom: 2px; }
        .phone-badge2-text { font-size: 0.6rem; color: var(--text-muted); white-space: nowrap; }

        /* STATS */
        .stats-section {
          padding: 0 5% 80px;
        }
        .stats-inner {
          max-width: 1200px; margin: 0 auto;
          display: grid; grid-template-columns: repeat(4, 1fr);
          gap: 1px; background: var(--border);
          border-radius: 20px; overflow: hidden;
          border: 1px solid var(--border);
        }
        .stat-item {
          background: var(--surface); padding: 32px 24px;
          text-align: center;
        }
        .stat-value {
          font-family: var(--font-display); font-size: 2.4rem;
          font-weight: 800; color: var(--teal); line-height: 1;
          margin-bottom: 8px; letter-spacing: -0.02em;
        }
        .stat-label { font-size: 0.85rem; color: var(--text-muted); font-weight: 400; }

        /* FEATURES */
        .features-section {
          padding: 100px 5%;
          position: relative; overflow: hidden;
        }
        .features-section::before {
          content: '';
          position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
          width: 800px; height: 800px; border-radius: 50%;
          background: radial-gradient(circle, rgba(26,110,248,0.04) 0%, transparent 70%);
          pointer-events: none;
        }
        .section-label {
          display: inline-flex; align-items: center; gap: 8px;
          font-size: 0.75rem; font-weight: 600; letter-spacing: 0.1em;
          text-transform: uppercase; color: var(--teal);
          margin-bottom: 16px;
        }
        .section-title {
          font-family: var(--font-display); font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 800; letter-spacing: -0.02em; line-height: 1.1;
          margin-bottom: 16px;
        }
        .section-desc { font-size: 1rem; color: var(--text-muted); max-width: 520px; line-height: 1.7; }
        .features-header { text-align: center; margin-bottom: 64px; }
        .features-header .section-desc { margin: 0 auto; }
        .features-grid {
          max-width: 1200px; margin: 0 auto;
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px;
        }
        .feature-card {
          background: var(--surface); border: 1px solid var(--border);
          border-radius: var(--radius); padding: 32px;
          transition: border-color 0.3s, transform 0.3s, box-shadow 0.3s;
          position: relative; overflow: hidden;
        }
        .feature-card::before {
          content: '';
          position: absolute; top: 0; left: 0; right: 0; height: 1px;
          background: linear-gradient(90deg, transparent, var(--teal), transparent);
          opacity: 0; transition: opacity 0.3s;
        }
        .feature-card:hover {
          border-color: rgba(0,212,200,0.2); transform: translateY(-4px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.3);
        }
        .feature-card:hover::before { opacity: 1; }
        .feature-icon {
          width: 52px; height: 52px; border-radius: 12px;
          background: var(--teal-dim); color: var(--teal);
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 20px;
        }
        .feature-title {
          font-family: var(--font-display); font-size: 1.1rem; font-weight: 700;
          margin-bottom: 10px; color: var(--text);
        }
        .feature-desc { font-size: 0.9rem; color: var(--text-muted); line-height: 1.6; }

        /* CTA DOWNLOAD */
        .download-section {
          padding: 100px 5%;
          position: relative; overflow: hidden;
        }
        .download-inner {
          max-width: 1200px; margin: 0 auto;
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 28px; padding: 72px 64px;
          display: grid; grid-template-columns: 1fr 1fr; gap: 64px;
          align-items: center; position: relative; overflow: hidden;
        }
        .download-inner::before {
          content: '';
          position: absolute; top: -100px; right: -100px;
          width: 400px; height: 400px; border-radius: 50%;
          background: radial-gradient(circle, rgba(0,212,200,0.07) 0%, transparent 70%);
        }
        .download-title {
          font-family: var(--font-display); font-size: clamp(1.8rem, 3vw, 2.6rem);
          font-weight: 800; letter-spacing: -0.02em; line-height: 1.2;
          margin-bottom: 20px;
        }
        .download-desc { color: var(--text-muted); line-height: 1.7; margin-bottom: 36px; font-size: 1rem; }
        .store-buttons { display: flex; gap: 16px; flex-wrap: wrap; }
        .store-btn {
          display: flex; align-items: center; gap: 12px;
          background: var(--surface2); border: 1px solid rgba(255,255,255,0.1);
          border-radius: 12px; padding: 12px 20px; text-decoration: none;
          transition: border-color 0.2s, transform 0.2s;
        }
        .store-btn:hover { border-color: var(--teal); transform: translateY(-2px); }
        .store-btn-icon { color: var(--text); }
        .store-btn-text { text-align: left; }
        .store-btn-sub { font-size: 0.6rem; color: var(--text-muted); display: block; line-height: 1; margin-bottom: 2px; letter-spacing: 0.03em; text-transform: uppercase; }
        .store-btn-name { font-family: var(--font-display); font-size: 0.95rem; font-weight: 700; color: var(--text); }
        .download-visual {
          position: relative; display: flex; justify-content: center;
        }
        .checklist { display: flex; flex-direction: column; gap: 18px; }
        .check-item {
          display: flex; align-items: flex-start; gap: 14px;
          padding: 18px 20px; background: var(--bg);
          border: 1px solid var(--border); border-radius: 12px;
        }
        .check-icon {
          width: 24px; height: 24px; border-radius: 50%;
          background: var(--teal-dim); color: var(--teal);
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; margin-top: 1px;
        }
        .check-text { font-size: 0.88rem; color: var(--text-muted); line-height: 1.5; }
        .check-text strong { color: var(--text); display: block; font-weight: 600; margin-bottom: 2px; font-size: 0.9rem; }

        /* FOOTER */
        .footer {
          padding: 48px 5%;
          border-top: 1px solid var(--border);
        }
        .footer-inner {
          max-width: 1200px; margin: 0 auto;
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 20px;
        }
        .footer-logo {
          display: flex; align-items: center; gap: 10px;
          font-family: var(--font-display); font-size: 1rem; font-weight: 700;
          color: var(--text); text-decoration: none;
        }
        .footer-links { display: flex; gap: 32px; list-style: none; flex-wrap: wrap; }
        .footer-links a {
          color: var(--text-muted); text-decoration: none;
          font-size: 0.85rem; transition: color 0.2s;
        }
        .footer-links a:hover { color: var(--text); }
        .footer-copy { font-size: 0.8rem; color: var(--text-muted); }

        /* RESPONSIVE */
        @media (max-width: 900px) {
          .nav-links { display: none; }
          .hamburger { display: flex; }
          .hero-inner { grid-template-columns: 1fr; gap: 48px; }
          .hero-visual { order: -1; }
          .stats-inner { grid-template-columns: repeat(2, 1fr); }
          .features-grid { grid-template-columns: 1fr 1fr; }
          .download-inner { grid-template-columns: 1fr; gap: 48px; padding: 40px; }
          .footer-inner { flex-direction: column; align-items: flex-start; }
        }
        @media (max-width: 600px) {
          .features-grid { grid-template-columns: 1fr; }
          .stats-inner { grid-template-columns: repeat(2, 1fr); }
          .phone-badge { display: none; }
          .phone-badge2 { display: none; }
          .hero { padding: 90px 5% 60px; }
        }

        /* ANIMATIONS */
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-1 { animation: fadeUp 0.7s ease both; }
        .animate-2 { animation: fadeUp 0.7s 0.1s ease both; }
        .animate-3 { animation: fadeUp 0.7s 0.2s ease both; }
        .animate-4 { animation: fadeUp 0.7s 0.3s ease both; }

        .teal-line {
          display: inline-block; width: 32px; height: 3px;
          background: var(--teal); border-radius: 2px; vertical-align: middle; margin-right: 10px;
        }
      `}</style>

      {/* NAV */}
      <nav className={`nav ${scrollY > 40 ? 'scrolled' : ''}`}>
        <a href="#" className="nav-logo">
          <div className="nav-logo-icon">
            <img src="/assets/images/logo.png" alt="Logo" style={{height:60,width:60}}/>
          </div>
          Medical  Dictionary
        </a>
        <ul className="nav-links">
          <li><a href="#">Home</a></li>
          <li><a href="#features">Features</a></li>
          <li><a href="#download">About</a></li>
          <li><a href="/privacy-policy">Privacy</a></li>
          <li><a href="#download" className="btn-nav">Download Free</a></li>
        </ul>
        <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
          <span style={menuOpen ? {transform:'rotate(45deg) translate(5px, 5px)'} : {}}></span>
          <span style={menuOpen ? {opacity:0} : {}}></span>
          <span style={menuOpen ? {transform:'rotate(-45deg) translate(5px, -5px)'} : {}}></span>
        </button>
      </nav>
      <div className={`mobile-menu ${menuOpen ? 'open' : ''}`}>
        <a href="#" onClick={() => setMenuOpen(false)}>Home</a>
        <a href="#features" onClick={() => setMenuOpen(false)}>Features</a>
        <a href="#download" onClick={() => setMenuOpen(false)}>About</a>
        <a href="/privacy-policy" onClick={() => setMenuOpen(false)}>Privacy</a>
        <a href="#download" style={{color:'var(--teal)', fontWeight:700}}>Download Free →</a>
      </div>

      {/* HERO */}
      <section className="hero" id="home">
        <div className="hero-bg">
          <div className="hero-gradient"></div>
          <div className="hero-gradient-2"></div>
          <div className="hero-grid"></div>
        </div>
        <div className="hero-inner">
          <div>
            <div className="hero-badge animate-1">
              <div className="hero-badge-dot"></div>
              Trusted by healthcare professionals
            </div>
            <h1 className="hero-title animate-2">
              Your Complete<br/>
              <span>Medical Reference</span><br/>
              at Your Fingertips
            </h1>
            <p className="hero-desc animate-3">
              The most comprehensive medical dictionary app for students, nurses, doctors, and allied health professionals. 50,000+ terms, abbreviations, and specialties — all offline.
            </p>
            <div className="hero-actions animate-4">
              <a href='#download' className="btn-primary-lg">
                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M3.18 23.76c.3.17.64.24.99.2L15.93 12 3.18.04a1.5 1.5 0 0 0-.99.2C1.76.65 1.5 1.3 1.5 2.1v19.8c0 .8.26 1.45.68 1.86z"/><path d="M20.06 10.2l-2.87-1.65-3.26 3.45 3.26 3.44 2.87-1.65c.82-.47.82-1.65 0-2.12l0 0z"/><path d="M3.18.04L15.46 10.8 5.02 1.28 3.18.04zM15.46 13.2L3.18 23.96l11.84-9.06L15.46 13.2z"/></svg>
                Get on Google Play
              </a>
              <a href="#features" className="btn-ghost-lg">
                Explore Features
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </a>
            </div>
          </div>
          <div className="hero-visual animate-3">
            <div className="phone-wrap">
              <div className="phone-frame">
                <div className="phone-screen">
                  <div className="phone-topbar">
                    <div className="phone-app-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="#04080f" strokeWidth="2.5" width="14" height="14"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                    </div>
                    <div>
                      <div className="phone-app-name">Medical  Dictionary</div>
                      <div className="phone-app-sub">Medical Reference</div>
                    </div>
                  </div>
                  <div className="phone-search">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                    Search terms, abbreviations…
                  </div>
                  <div className="phone-term-list">
                    {[
                      {name: "Tachycardia", def: "Rapid heart rate exceeding 100 bpm at rest"},
                      {name: "Dyspnea", def: "Difficulty or labored breathing"},
                      {name: "Bradycardia", def: "Abnormally slow heart rate below 60 bpm"},
                      {name: "Hypertension", def: "Persistently elevated arterial blood pressure"},
                      {name: "Edema", def: "Abnormal accumulation of fluid in tissue"},
                    ].map((t, i) => (
                      <div className="phone-term" key={i}>
                        <div className="phone-term-dot"></div>
                        <div>
                          <div className="phone-term-name">{t.name}</div>
                          <div className="phone-term-def">{t.def}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="phone-glow"></div>
              <div className="phone-badge">
                <div className="phone-badge-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                </div>
                <div className="phone-badge-text">
                  <div className="phone-badge-num">50K+</div>
                  <div className="phone-badge-label">Medical Terms</div>
                </div>
              </div>
              <div className="phone-badge2">
                <div className="phone-badge2-stars">★★★★★</div>
                <div className="phone-badge2-text">Rated 4.8 on Play Store</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <div className="stats-section">
        <div className="stats-inner">
          {stats.map((s, i) => (
            <div className="stat-item" key={i}>
              <div className="stat-value">{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* FEATURES */}
      <section className="features-section" id="features">
        <div className="features-header">
          <div className="section-label">
            <span className="teal-line"></span>
            Features
          </div>
          <h2 className="section-title">Everything You Need to Know Medicine</h2>
          <p className="section-desc">Built for healthcare professionals who need accurate, fast, and comprehensive medical references on the go.</p>
        </div>
        <div className="features-grid">
          {features.map((f, i) => (
            <div className="feature-card" key={i}>
              <div className="feature-icon">{f.icon}</div>
              <div className="feature-title">{f.title}</div>
              <p className="feature-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* DOWNLOAD CTA */}
      <section className="download-section" id="download">
        <div className="download-inner">
          <div style={{position:'relative', zIndex:1}}>
            <div className="section-label"><span className="teal-line"></span>Download</div>
            <h2 className="download-title">Ready to Master Medical Terminology?</h2>
            <p className="download-desc">Join thousands of healthcare professionals and students who rely on Medical  Dictionary every day. Free to download, always up-to-date.</p>
            <div className="store-buttons">
              <a href={GOOGLE_PLAY_URL} target="_blank" className="store-btn">
                <div className="store-btn-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22"><path d="M3.18 23.76c.3.17.64.24.99.2L15.93 12 3.18.04a1.5 1.5 0 0 0-.99.2C1.76.65 1.5 1.3 1.5 2.1v19.8c0 .8.26 1.45.68 1.86z"/><path d="M20.06 10.2l-2.87-1.65-3.26 3.45 3.26 3.44 2.87-1.65c.82-.47.82-1.65 0-2.12z"/><path d="M3.18.04L15.46 10.8 5.02 1.28 3.18.04zM15.46 13.2L3.18 23.96l11.84-9.06L15.46 13.2z"/></svg>
                </div>
                <div className="store-btn-text">
                  <span className="store-btn-sub">Get it on</span>
                  <span className="store-btn-name">Google Play</span>
                </div>
              </a>
              <a href={APP_STORE_URL} target="_blank" className="store-btn">
                <div className="store-btn-icon">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                </div>
                <div className="store-btn-text">
                  <span className="store-btn-sub">Download on the</span>
                  <span className="store-btn-name">App Store</span>
                </div>
              </a>
            </div>
          </div>
          <div className="checklist" style={{position:'relative', zIndex:1}}>
            {[
              {title: "50,000+ Medical Terms", desc: "The most complete dictionary available on mobile."},
              {title: "Works Offline", desc: "Access your full dictionary without an internet connection."},
              {title: "Regular Updates", desc: "New terms and definitions added with each release."},
              {title: "Prometric Exam Prep", desc: "Practice quizzes to prepare for nursing and medical exams."},
            ].map((c, i) => (
              <div className="check-item" key={i}>
                <div className="check-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12"><path d="M20 6L9 17l-5-5"/></svg>
                </div>
                <div className="check-text">
                  <strong>{c.title}</strong>
                  {c.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <div className="footer-inner">
          <a href="#" className="footer-logo">
            <div className="nav-logo-icon" style={{width:28, height:28, borderRadius:8}}>
              <svg viewBox="0 0 24 24" fill="none" stroke="#04080f" strokeWidth="2.5" width="14" height="14"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
            </div>
            Medical  Dictionary
          </a>
          <ul className="footer-links">
            <li><a href="#">Home</a></li>
            <li><a href="#features">Features</a></li>
            <li><a href="/privacy-policy">Privacy Policy</a></li>
            <li><a href="https://www.facebook.com/nursingprometric994" target="_blank">Contact</a></li>
          </ul>
          <span className="footer-copy">© {new Date().getFullYear()} Medical  Dictionary. All rights reserved.</span>
        </div>
      </footer>
    </>
  )
}